import { Thermometer, AlertCircle, Flower2, Sun, Leaf, Snowflake } from 'lucide-react';
import GuideHero from '../../components/guides/GuideHero';
import GuideCTA from '../../components/guides/GuideCTA';

const riseTypes = [
  { title: 'Gentle Sips (Mayflies/Small Midges)', items: ['Fish barely breaks surface', 'Small, quiet dimple', 'Often repeated in same spot', 'Fish are being selective', 'Action: Use smaller flies, longer leaders, gentle presentations'] },
  { title: 'Splashy Rises (Caddisflies)', items: ["Fish comes up aggressively", "Water splashes, sometimes you see the fish's back", 'Caddis adults are trying to escape quickly', 'Fish must react fast', "Action: Use larger flies, normal leaders, don't worry about perfect presentation"] },
  { title: 'Head-and-Tail Rises (Large Mayflies)', items: ["You see the fish's head, then its back and tail", 'Fish is taking something big just under the surface', 'Usually during heavy mayfly hatches', 'Action: Use large dry flies or emergers'] },
  { title: 'Porpoising (Emerging Insects)', items: ['Multiple rises in quick succession', 'Fish following insects as they emerge and drift', 'Common during caddis hatches', 'Action: Use emerger patterns or soft-hackle flies'] },
  { title: 'No Rises (Subsurface Feeding)', items: ['Most common situation', 'Fish are eating nymphs, larvae, or other underwater food', 'Look for subtle flashes underwater', 'Action: Use nymph patterns with indicators or tight-line techniques'] },
];

const flyChoices = [
  { title: 'Active Hatch Scenario:', text: 'Insects on water + rising fish = match the hatch', tips: ['Use dry flies that imitate what you see', 'Size and color matter more than exact species'] },
  { title: 'Pre-Hatch Scenario:', text: 'Some insects around but no rises = use emergers', tips: ['Fish are intercepting insects trying to reach surface', 'Focus on the surface film'] },
  { title: 'No Surface Activity:', text: 'No insects, no rises = go subsurface', tips: ['Use nymph patterns or streamers', 'Focus on likely holding spots'] },
  { title: 'Terrestrial Conditions:', text: 'Windy day + terrestrial insects = opportunity', tips: ['Use ant, hopper, or beetle patterns', 'Fish close to grassy banks'] },
];

const seasons = [
  { Icon: Flower2, color: 'green', label: 'Spring (April-May)', tips: ['Water temperature is key - fish become active around 10\u00B0C', 'Early stoneflies emerge when water hits 12-15\u00B0C', 'Focus on sunny, protected areas that warm first', 'Midges are often the only consistent hatch'] },
  { Icon: Sun, color: 'yellow', label: 'Summer (June-August)', tips: ['Peak insect activity, especially evenings', 'Caddis and mayfly hatches are most reliable', 'Terrestrial activity increases, especially after rain', 'Fish early morning and evening when insects are most active'] },
  { Icon: Leaf, color: 'orange', label: 'Fall (September-October)', tips: ['Large mayflies (like Blue Wing Olives) extend the season', 'Terrestrials become more important as temperatures drop', 'Fish feed heavily before winter', 'Focus on warmer parts of the day'] },
  { Icon: Snowflake, color: 'blue', label: 'Winter (November-March)', tips: ['Midges are the primary insect activity', 'Fish are lethargic but still feed on warm days', 'Focus on deeper pools and slower water', 'Tiny patterns (#20-24) are essential'] },
];

const tempTriggers = [
  { temp: '4\u00B0C', effect: 'Fish become minimally active' },
  { temp: '8\u00B0C', effect: 'Midge activity begins' },
  { temp: '12\u00B0C', effect: 'Stonefly emergence starts' },
  { temp: '15\u00B0C', effect: 'Mayfly and caddis activity increases significantly' },
  { temp: '18\u00B0C (64\u00B0F)', effect: 'Peak insect activity in most waters' },
  { temp: '22\u00B0C+', effect: 'Some insects become less active, fish deeper' },
];

const waterTypes = [
  { title: 'Fast Riffles', tips: ['Stonefly nymph habitat', 'High oxygen supports lots of aquatic life', 'Fish nymphs dead-drift through feeding lanes', 'Look for pocket water behind rocks'] },
  { title: 'Deep Pools', tips: ['Mayfly nymph habitat', 'Fish often hold deep during midday', 'Early morning and evening surface activity', 'Use sinking lines or weighted nymphs during day'] },
  { title: 'Slow Eddies', tips: ['Concentrate insects and food', 'Great spots during hatches', 'Fish cruise these areas looking for easy meals', 'Perfect for dry fly fishing during activity'] },
  { title: 'Undercut Banks', tips: ['Terrestrial insect habitat', 'Fish feel secure in cover', 'Great for hopper, ant, and beetle patterns', 'Approach quietly and cast accurately'] },
];

const mistakes = [
  { title: 'Not Spending Enough Time Observing', text: 'Most anglers spend 30 seconds looking, then start fishing. Spend at least 10 minutes observing before making your first cast.' },
  { title: 'Focusing Only on Rising Fish', text: 'Rising fish are obvious, but most feeding happens underwater. Don\'t ignore subsurface opportunities.' },
  { title: 'Ignoring Weather Conditions', text: 'Barometric pressure, wind, and cloud cover all affect insect activity. Overcast days often produce better hatches.' },
  { title: 'Not Adapting to Changing Conditions', text: 'Water reading is continuous. What worked an hour ago might not work now. Keep observing and adapting.' },
  { title: 'Overlooking Terrestrials', text: 'North American summers offer excellent terrestrial fishing, but many anglers focus only on aquatic insects.' },
];

const proTips = [
  { title: 'Temperature Matters', text: 'Our colder waters mean insect activity is more temperature-dependent. Carry a stream thermometer and learn the trigger temperatures for your local waters.' },
  { title: 'Weather Windows', text: 'Weather changes quickly across North America. Learn to recognize the weather patterns that trigger insect activity in your area.' },
  { title: 'Local Knowledge', text: 'Every watershed has unique characteristics. Spend time with local anglers and fly shop owners to learn regional patterns.' },
  { title: 'Timing is Everything', text: 'Our shorter seasons mean timing is critical. Learn when specific insects are active in your area and plan your fishing accordingly.' },
  { title: 'Adapt Southern Techniques', text: 'Much fly fishing literature focuses on American waters. Learn to adapt techniques for our colder, shorter seasons.' },
];

const ReadingWater = () => (
  <div className="min-h-screen bg-[#0F1419]">
    <GuideHero
      title="Reading The Water"
      subtitle="How to Determine What Fish Are Eating"
      description="The key to successful fly fishing is observation. Most anglers rush to the water and start casting without taking time to read what's happening. This is a mistake. The most successful fly fishers are detectives first, anglers second."
    />

    {/* Why Reading Water Matters */}
    <section className="py-16 bg-[#1E2328]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white mb-6">Why Reading Water Matters</h2>
        <div className="prose prose-lg max-w-none text-slate-300 space-y-4">
          <p>Fish are opportunistic feeders, but they're also energy-conscious. They position themselves where food comes to them with minimal effort. Understanding where fish feed and what they're eating gives you a massive advantage over anglers who just cast randomly.</p>
          <p>In North American waters, this skill is critical for success. Regional variations in seasons and water temperatures mean fish have specific feeding windows, and understanding your local conditions is key.</p>
        </div>
      </div>
    </section>

    {/* 4-Step System */}
    <section className="py-16 bg-[#151b21]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">The 4-Step System for Reading Water</h2>
        </div>

        {/* Step 1: Observe Banks */}
        <div className="mb-16">
          <div className="bg-[#1E2328] rounded-3xl shadow-xl overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-8 py-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1E2328]/20 rounded-full flex items-center justify-center flex-shrink-0"><span className="text-2xl font-bold text-white">1</span></div>
              <h3 className="text-3xl font-bold text-white">Observe the Banks (Before You Even Touch Your Rod)</h3>
            </div>
            <div className="p-8">
              <p className="text-lg text-slate-300 mb-6">Walk along the stream for at least 10 minutes before fishing. This reconnaissance pays huge dividends.</p>
              <h4 className="text-xl font-bold text-white mb-4">What to look for:</h4>
              <ul className="space-y-3 mb-6">
                {['Shake bushes and overhanging vegetation - note what insects fall out', 'Check spider webs - they trap flying insects and show what\'s active', 'Look at the ground near the water - are there insect husks from recent hatches?', 'Note the size, shape, and color of terrestrial insects', 'Check for grasshoppers in late summer (especially on windy days)', 'Look for ants, beetles, and other land insects'].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gradient-to-r from-[#137dc5]/15 to-[#137dc5]/5 rounded-xl p-6">
                <h4 className="font-bold text-[#3aa0e0] mb-2">Pro Tip:</h4>
                <p className="text-slate-300">In early spring, focus on areas where snow has recently melted. These spots warm first and attract the earliest insect activity.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Check Surface */}
        <div className="mb-16">
          <div className="bg-[#1E2328] rounded-3xl shadow-xl overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-8 py-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1E2328]/20 rounded-full flex items-center justify-center flex-shrink-0"><span className="text-2xl font-bold text-white">2</span></div>
              <h3 className="text-3xl font-bold text-white">Check the Water Surface</h3>
            </div>
            <div className="p-8">
              <p className="text-lg text-slate-300 mb-6">Before looking for rising fish, study what's actually on the water.</p>
              <h4 className="text-xl font-bold text-white mb-4">Surface indicators:</h4>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3"><div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div><span className="text-slate-300">Floating insects (note their size, color, and wing position)</span></li>
                <li className="flex items-start gap-3 ml-6"><div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0"></div><span className="text-slate-400">Mayflies have upright wings like tiny sailboats</span></li>
                <li className="flex items-start gap-3 ml-6"><div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0"></div><span className="text-slate-400">Caddisflies have tent-like wings that fold back over their bodies</span></li>
                <li className="flex items-start gap-3 ml-6"><div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0"></div><span className="text-slate-400">Midges are tiny and often cluster in groups</span></li>
                <li className="flex items-start gap-3"><div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div><span className="text-slate-300">Spent insects (dead or dying) create feeding opportunities</span></li>
                <li className="flex items-start gap-3"><div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0"></div><span className="text-slate-300">Foam lines and eddies concentrate insects</span></li>
              </ul>
              <div className="bg-[#151b21] rounded-xl p-6">
                <h4 className="text-xl font-bold text-white mb-4">What different insects look like on water:</h4>
                <div className="space-y-2 text-slate-300">
                  <p><strong>Mayflies:</strong> Upright wings, long tails, delicate appearance</p>
                  <p><strong>Caddisflies:</strong> Tent wings, often skittering across surface</p>
                  <p><strong>Midges:</strong> Tiny, usually in clusters, long legs</p>
                  <p><strong>Terrestrials:</strong> Various shapes, often struggling on surface</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Rising Fish */}
        <div className="mb-16">
          <div className="bg-[#1E2328] rounded-3xl shadow-xl overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-8 py-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1E2328]/20 rounded-full flex items-center justify-center flex-shrink-0"><span className="text-2xl font-bold text-white">3</span></div>
              <h3 className="text-3xl font-bold text-white">Watch for Rising Fish (The Most Important Skill)</h3>
            </div>
            <div className="p-8">
              <p className="text-lg text-slate-300 mb-8">Different rise forms tell you exactly what fish are eating and how to approach them.</p>
              <div className="space-y-6">
                {riseTypes.map((rise) => (
                  <div key={rise.title} className="border-l-4 border-cyan-500 pl-6 py-2">
                    <h4 className="text-xl font-bold text-white mb-2">{rise.title}</h4>
                    <ul className="space-y-2 text-slate-300">
                      {rise.items.map((item) => <li key={item}>&bull; {item}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: Fly Choice */}
        <div className="mb-16">
          <div className="bg-[#1E2328] rounded-3xl shadow-xl overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-8 py-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1E2328]/20 rounded-full flex items-center justify-center flex-shrink-0"><span className="text-2xl font-bold text-white">4</span></div>
              <h3 className="text-3xl font-bold text-white">Make Your Fly Choice</h3>
            </div>
            <div className="p-8">
              <p className="text-lg text-slate-300 mb-6">Based on your observations, choose your approach:</p>
              <div className="grid md:grid-cols-2 gap-6">
                {flyChoices.map((c) => (
                  <div key={c.title} className="bg-gradient-to-br from-cyan-50 to-white border border-cyan-200 rounded-xl p-6">
                    <h4 className="font-bold text-[#3aa0e0] mb-3">{c.title}</h4>
                    <p className="text-slate-300 mb-2">{c.text}</p>
                    <ul className="space-y-1 text-sm text-slate-400">
                      {c.tips.map((t) => <li key={t}>&bull; {t}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Advanced Techniques */}
    <section className="py-16 bg-[#1E2328]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-white mb-12 text-center">Advanced Water Reading Techniques</h2>

        <div className="mb-12">
          <h3 className="text-3xl font-bold text-white mb-6">Reading Seasonal Patterns</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {seasons.map(({ Icon, color, label, tips }) => (
              <div key={label} className="bg-[#151b21] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 text-${color}-600`} />
                  </div>
                  <h4 className="text-xl font-bold text-white">{label}</h4>
                </div>
                <ul className="space-y-2 text-slate-300">
                  {tips.map((t) => <li key={t}>&bull; {t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <h3 className="text-3xl font-bold text-white mb-6">Water Temperature and Insect Activity</h3>
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 rounded-2xl p-8 text-white">
            <p className="text-lg mb-6">Understanding temperature triggers helps predict activity:</p>
            <div className="grid md:grid-cols-2 gap-4">
              {tempTriggers.map(({ temp, effect }) => (
                <div key={temp} className="flex items-center gap-3">
                  <Thermometer className="w-6 h-6 flex-shrink-0" />
                  <span><strong>{temp}:</strong> {effect}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h3 className="text-3xl font-bold text-white mb-6">Reading Different Water Types</h3>
          <div className="space-y-4">
            {waterTypes.map(({ title, tips }) => (
              <div key={title} className="bg-[#1E2328] border-2 border-white/10 rounded-xl p-6">
                <h4 className="text-xl font-bold text-white mb-3">{title}</h4>
                <ul className="space-y-2 text-slate-300">
                  {tips.map((t) => <li key={t}>&bull; {t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Common Mistakes */}
    <section className="py-16 bg-red-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <AlertCircle className="w-10 h-10 text-red-600" />
          <h2 className="text-4xl font-bold text-white">Common Mistakes in Reading Water</h2>
        </div>
        <div className="space-y-6">
          {mistakes.map((m, i) => (
            <div key={m.title} className="bg-[#1E2328] rounded-xl p-6 shadow-md">
              <h3 className="text-xl font-bold text-red-600 mb-2">Mistake {i + 1}: {m.title}</h3>
              <p className="text-slate-300">{m.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Pro Tips */}
    <section className="py-16 bg-[#0b0f13] text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold mb-8 text-center">Pro Tips for North American Waters</h2>
        <div className="space-y-6">
          {proTips.map((tip) => (
            <div key={tip.title} className="border-l-4 border-cyan-400 pl-6">
              <h3 className="text-2xl font-bold mb-2">{tip.title}</h3>
              <p className="text-slate-300">{tip.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Putting It Together */}
    <section className="py-16 bg-[#1E2328]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-white mb-6">Putting It All Together</h2>
        <div className="prose prose-lg max-w-none text-slate-300 space-y-4 mb-8">
          <p>Reading water is a skill that improves with practice. Start by spending more time observing and less time casting. Keep a fishing journal noting:</p>
          <ul className="space-y-2">
            {['Date and time', 'Water temperature', 'Weather conditions', 'Insects observed', 'Fish behavior', 'Successful flies and techniques'].map((item) => (
              <li key={item}>&bull; {item}</li>
            ))}
          </ul>
          <p>Over time, you'll develop the ability to read water quickly and choose the right approach for any situation.</p>
        </div>
      </div>
    </section>

    <GuideCTA
      title="Ready for Real-Time Intelligence?"
      description="The FlyFinder app takes water reading to the next level by combining your observations with real-time weather data, seasonal patterns, and location-specific insect activity. It's like having an expert guide who knows your local waters in your pocket."
    />
  </div>
);

export default ReadingWater;
